import { LiveAnnouncer } from '@angular/cdk/a11y';
import { AfterViewInit, Component, ElementRef, Inject, LOCALE_ID, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom, Observable, timer } from 'rxjs';
import { ApiService, changeMfaMethodAPI } from '../api/api.service';
import { BallotListService } from '../ballot-list/ballot-list.service';
import { dashboardRoute, idUploadRoute } from '../route-names';
import { ApiAboutService } from '../Services/api-about.service';
import { ScreenOrderService } from '../Services/screen-order.service';
import { SessionService } from '../Services/session.service';
import { GenericDialogComponent } from '../shared/generic-dialog/generic-dialog.component';
import { ElectorDataService, MfaMethod } from '../Services/elector-data.service';
import { DialogType } from '../Services/dialog.service';

@Component({
    selector: 'app-verify-mfa',
    templateUrl: './verify-mfa.component.html',
    styleUrl: './verify-mfa.component.scss',
    standalone: false
})
export class VerifyMfaComponent implements AfterViewInit {
  @ViewChildren('mfaRef') mfaInputFields!: QueryList<ElementRef>;
  routeName = "";
  mfaFormGroup: FormGroup;

  readonly alreadyVoted = new BehaviorSubject(false);
  alreadyVotedTime = "";
  errorText?: string;
  errorTitle?: string;
  electionOpen: boolean = true;
  readonly electionStartTime: Date;
  readonly electionEndTime: Date;

  public displaySpinner: boolean = false;

  maxMFACodeAttemptsReached: boolean = false;
  maxMFAResendsReached: boolean = false;

  numbers = [0, 1, 2, 3, 4, 5];
  resendAllowedTimer: Observable<number> | undefined;
  allowResends: boolean = false;

  constructor(
    private readonly dialog: MatDialog,
    private formBuilder: FormBuilder,
    readonly aboutService: ApiAboutService,
    @Inject(LOCALE_ID) readonly locale: string,
    private readonly router: Router,
    readonly api: ApiService,
    public electorDataService: ElectorDataService,
    readonly session: SessionService,
    readonly ballotListService: BallotListService,
    private readonly screenOrderService: ScreenOrderService,
    private readonly announcer: LiveAnnouncer,
  ) {
    if (this.aboutService.electionClosed) {
      this.errorTitle = $localize`Election Closed`;
      this.errorText = $localize`The election was open from ${this.aboutService.startTime} to ${this.aboutService.endTime}.`;
      this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
      this.electionOpen = false;
    }
    if (this.session.activeSessionCheck()) {
      this.errorTitle = $localize`Active Session`;
      this.errorText = $localize`Someone is already logged in on this browser. Please close this tab and log in on another browser or another device.`;
      this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
    }
    this.mfaFormGroup = this.formBuilder.group({
      code: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]]
    });

    this.electionStartTime = new Date(2000, 1, 1);
    let start: string | undefined = this.aboutService.aboutInformation.value.org_feature_set.miscSettings?.electionStartTimestamp;
    if (start) {
      this.electionStartTime = new Date(start);
    }
    // I doubt anyone will use this after the year 100,000. 
    this.electionEndTime = new Date(100000, 1, 1);
    let expiry: string | undefined = this.aboutService.aboutInformation.value.org_feature_set.miscSettings?.electionEndTimestamp;
    if (expiry) {
      this.electionEndTime = new Date(expiry);
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.routeName = this.router.url
    }, 1);

    if(this.electorDataService.userAuthData()?.mfaMethod !== 'APP') {
      this.resetTimer();
    }
    if(this.electorDataService.userAuthData()?.attemptsRemaining == 0){
      this.maxMFACodeAttemptsReached = true;
    }
    if(this.electorDataService.userAuthData()?.resendsRemaining == 0){
      this.maxMFAResendsReached = true;
    }
  }

  isValid(): boolean {
    return this.mfaFormGroup.valid;
  }

  async submit() {
    if (this.session.activeSessionCheck()) {
      this.errorTitle = $localize`Active Session`;
      this.errorText = $localize`Someone is already logged in on this browser. Please close this tab and log in on another browser or another device.`;
      this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
      return false;
    }
    if ((this.electorDataService.userAuthData()?.attemptsRemaining ?? 0) <= 0) {
      this.errorText = $localize`You have exhausted attempts to enter the verification code. Please use the "Contact Support" link for assistance.`;
      this.errorTitle = $localize`Maximum Attempts Reached.`;
      this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
      return false;
    }

    this.displaySpinner = true;
    this.session.authenticateFromMFA(this.mfaFormGroup.get('code')?.value).subscribe({
      next: async (response) => {
        this.screenOrderService.setVoterBallotType(response?.ballotType);
        this.ballotListService.setBallots(response);
        this.aboutService.signatureRequired.next(response?.events?.at(0)?.voteEventData?.optionalEventConfiguration?.submitSignature ?? false);
        this.electorDataService.user = response?.elector;
        this.electorDataService.setIdvStatus(response?.elector);
        this.electorDataService.receiptCode.set(response?.elector?.receipt_code);
        this.displaySpinner = false;

        if (this.electorDataService.userAuthData()) {
          this.electorDataService.userAuthData()!.mfaVerified = true;
          this.electorDataService.userAuthData()!.attemptsRemaining = this.electorDataService.userAuthData()?.maxAttempts ?? this.electorDataService.MAX_OTP_ATTEMPTS;
          this.electorDataService.userAuthData()!.resendsRemaining = this.electorDataService.userAuthData()?.maxResends ?? this.electorDataService.MAX_OTP_RESENDS;
          this.electorDataService.restartOrgVerifyLockoutTimer();
        }
        this.electorDataService.orgVerifications.set(response?.orgVerifications ?? []);
      },
      error: (err) => {
        this.resetMfaInput();
        this.displaySpinner = false;
        switch (err.error_type) {

          case 'ALREADY_VOTED':
            let date = new Date(err.time);
            let time = date.toLocaleTimeString(this.locale) + ", " + date.toLocaleDateString(this.locale, { year: 'numeric', month: 'long', day: 'numeric' });
            this.alreadyVotedTime = time;
            this.alreadyVoted.next(true);
            break;

          case 'INVALID_IDENTITY':
            this.errorText = $localize`The information provided does not match our records. Please verify that you have entered the information correctly. If you believe this is in error, use the "Contact Support" link for assistance.`
            this.openDialog({ type: DialogType.ERROR, title: $localize`Information not found`, content: this.errorText });
            break;

          case 'BALLOT_NOT_AVAILABLE':
            this.errorText = $localize`Your ballot isn't available yet. Please check back later. If you believe this is in error, use the "Contact Support" link for assistance.`;
            this.errorTitle = $localize`Ballot not available.`
            this.openDialog({ type: DialogType.ALERT, title: this.errorTitle, content: this.errorText });
            break;

          case 'DECODER_STATE_LOST':
            this.errorText = $localize`Your session timed out. Please try to submit again or refresh the page.`;
            this.errorTitle = $localize`Verification Not Successful.`
            this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
            break;

          case 'INVALID_MTE_DECODING':
            this.errorText = $localize`Your session timed out. Please try to submit again or refresh the page.`;
            this.errorTitle = $localize`Verification Not Successful.`
            this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
            break;

          case 'NOT_LOGGED_IN':
            this.errorText = $localize`There was an error with session cookies in this browser. To continue, please delete your cookies or try logging in from another device.`;
            this.errorTitle = $localize`Invalid Session Data.`
            this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
            break;
          case 'MISSING_CSRF_TOKEN':
            this.errorText = $localize`There was an error with session cookies in this browser. To continue, please delete your cookies or try logging in from another device.`;
            this.errorTitle = $localize`Invalid Session Data.`
            this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
            break;
          case 'OTP_EXPIRED':
            this.electorDataService.userAuthData()!.attemptsRemaining = (this.electorDataService.userAuthData()?.attemptsRemaining ?? 0) - 1;
            if ((this.electorDataService.userAuthData()?.attemptsRemaining ?? 0) <= 0) {
              this.errorText = $localize`You have exhausted attempts to enter the verification code, to continue, please press the OK button to return to the login screen and try to enter again.`;
              this.errorTitle = $localize`Maximum Attempts Reached.`
            } else {
              this.errorText = $localize`The verification code has expired. Please submit a new code. `
              if(this.electorDataService.userAuthData()?.attemptsRemaining == 1){
                this.errorText = this.errorText + $localize`You have 1 attempt remaining.`
              } else {
                this.errorText = this.errorText + $localize`You have ${this.electorDataService.userAuthData()?.attemptsRemaining ?? 5} attempts remaining.`;
              }
              this.errorTitle = $localize`Code Expired.`
            }
            this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
            break;
          case 'INVALID_OTP':
            this.electorDataService.userAuthData()!.attemptsRemaining = (this.electorDataService.userAuthData()?.attemptsRemaining ?? 0) - 1;
            this.errorText = $localize`The verification code is incorrect. To continue, please try entering it again. `;
            if(this.electorDataService.userAuthData()?.attemptsRemaining == 1){
              this.errorText = this.errorText + $localize`You have 1 attempt remaining.`
            } else {
              this.errorText = this.errorText + $localize`You have ${this.electorDataService.userAuthData()?.attemptsRemaining ?? 5} attempts remaining.`;
            }
            this.errorTitle = $localize`Invalid Code`
            this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
            break
          case 'MAX_OTP_ATTEMPTS':
            this.electorDataService.userAuthData()!.attemptsRemaining = 0;
            this.maxMFACodeAttemptsReached = true;
            this.errorText = $localize`Your account has been temporarily blocked. To unblock it, please use the "Contact Support" link for assistance.`;
            this.errorTitle = $localize`Maximum Attempts Reached.`
            this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
            break;
          case 'VERIFY_OTP_TOO_SOON':
            this.errorTitle = $localize`Verification too soon.`;
            this.errorText = $localize`Important! Please wait at least ${this.electorDataService.mfaExpirationTimeMinutes()} minutes before entering your verification code again.`;
            this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
            break;
          case 'RESEND_TOO_SOON':
            this.errorTitle = $localize`Resend too soon.`;
            this.errorText = $localize`Please wait at least ${this.electorDataService.resendExpirationTimeMinutes()} minutes before requesting a new verification code.`;
            this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
            break;
          case 'MAX_OTP_RESENDS':
            this.errorText = $localize`You have made ${this.electorDataService.userAuthData()?.maxResends ?? 5} resend requests. No more requests are allowed.`;
            this.errorTitle = $localize`Maximum Attempts Reached.`
            this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
            break;
          case 'UNKNOWN_USER':
            this.electorDataService.userAuthData()!.attemptsRemaining = (this.electorDataService.userAuthData()?.attemptsRemaining ?? 0) - 1;
            this.errorTitle = $localize`Information not found`;
            this.errorText = $localize`The information provided does not match our records. Please verify that you have entered the information correctly. If you believe this is in error, use the "Contact Support" link for assistance.`
            this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText })
            break;
          case 'BAD_REQUEST':
            this.errorText = $localize`Please try again or use the "Contact Support" link for assistance.`;
            this.errorTitle = $localize`Bad Request.`
            this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
            break;
          case "ALREADY_LOGGED_IN":
            this.errorText = $localize`You already have an active session. To continue, please close the active session and try again.`;
            this.errorTitle = $localize`Already Logged In.`
            this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
            break;
          case 'ELECTION_CLOSED':
            this.errorText = $localize`The election was open from ${this.aboutService.startTime} to ${this.aboutService.endTime}.`;
            this.errorTitle = $localize`Election Closed.`
            this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
            break;
          case 'MISSING_EMAIL':
            this.errorText = $localize`We could not find an email address on file. Please use the "Contact Support" link for assistance.`;
            this.errorTitle = $localize`Missing Email`
            this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
            break;

          case 'MISSING_PHONE':
            this.errorText = $localize`We could not find a phone number on file. Please use the "Contact Support" link for assistance.`;
            this.errorTitle = $localize`Missing Phone`
            this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
            break;

          case 'INVALID_FORM':
            this.errorText = $localize`We could not process your request. Please use the "Contact Support" link for assistance.`;
            this.errorTitle = $localize`Malformed Data`
            this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
            break;
          case 'INVALID_API':
            this.errorText = $localize`There was an error with session cookies in this browser. To continue, please try logging in again.`;
            this.errorTitle = $localize`Invalid Session Data.`
            this.openDialog({ type: DialogType.ALERT, title: this.errorTitle, content: this.errorText });
            break;
          default:
            this.errorText = $localize`An unknown error occurred. Please use the "Contact Support" link for assistance.`
            this.errorTitle = $localize`Unknown error.`
            this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
            break;
        }
      },
      complete: () => {
        this.announcer.announce($localize`Verification Complete`, "assertive");
        setTimeout(() => {
          this.displaySpinner = false;
          if(this.aboutService.VWAFlowType === 'GLOBAL') {
            this.router.navigate(dashboardRoute);
          } else if (this.aboutService.VWAFlowType === 'ORG_1_STEP_MFA') {
            this.router.navigate(this.screenOrderService.nextRoute('home'), {
              queryParams: { status: 'success' }
            });
          } else if(['NONE', 'RETRY'].includes(this.electorDataService.idvStatus())) {
            if(this.electorDataService.idvStatus() === 'RETRY'){
              this.errorTitle = $localize`Retry Verification`
              this.errorText = $localize`Your identity could not be verified. Please try again.`;
              this.openDialog({ type: DialogType.ALERT, title: this.errorTitle, content: this.errorText });
            }
            this.router.navigate(idUploadRoute);
          } else if( this.electorDataService.idvStatus() === 'FAILED'){
            this.errorTitle = $localize`Verification Failed`
            this.errorText = $localize`Your identity verification failed. If you believe this is in error, please use the "Contact Support" link for assistance.`;
            this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
            this.router.navigate(dashboardRoute);
          } else {
            this.router.navigate(dashboardRoute);
          }
          this.resetFocus();
        }, 500);
      },
    })
    return this.electorDataService.userAuthData()?.mfaVerified;
  }

  resetTimer() {
    this.allowResends = false;
    this.resendAllowedTimer = timer(this.electorDataService.userAuthData()?.resendExpirationTime ?? 5*60*1000);
    this.resendAllowedTimer.subscribe((done) => {
      this.allowResends = true
    })
  }

  resendOTP() {
    this.resetMfaInput();
    let now = Date.now();
    if(now < (this.electorDataService.userAuthData()?.resendExpiration ?? 0) ){
      this.errorTitle = $localize`Resend too soon.`;
      this.errorText = $localize`Please wait at least ${this.electorDataService.resendExpirationTimeMinutes()} minutes before requesting a new verification code.`;
      this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
    }
    if ((this.electorDataService.userAuthData()?.resendsRemaining ?? 0) <= 0) {
      this.maxMFAResendsReached = true;
      this.errorText = $localize`You have made ${this.electorDataService.userAuthData()?.maxResends ?? 5 } resend requests. No more requests are allowed.`;
      this.errorTitle = $localize`Maximum Attempts Reached.`;
      this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
    } else {
      this.postMFAMethod(this.electorDataService.userAuthData()?.voter_id, 'EMAIL');
      this.electorDataService.userAuthData()!.resendsRemaining = (this.electorDataService.userAuthData()?.resendsRemaining ?? 1) - 1;
    }
  }

  async postMFAMethod(id: string | undefined, authMethod: MfaMethod): Promise<boolean> {
    if (id === undefined) {
      this.errorTitle = $localize`Information not found`;
      this.errorText = $localize`The information provided does not match our records. Please verify that you have entered the information correctly. If you believe this is in error, use the "Contact Support" link for assistance.`
      this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText })
      return false;
    }

    if(this.electorDataService.userAuthData() === undefined) {
      this.electorDataService.userAuthData.set({
        voter_id: id,
        mfaMethod: authMethod,
        mfaVerified: false
      });
    } else {
      this.electorDataService.userAuthData()!.mfaMethod = authMethod;
    }
    try {
      this.displaySpinner = true;
      let response = await firstValueFrom(this.api.postWithHandshakeCheck<{ token: string }>(changeMfaMethodAPI, {
        voter_id: id,
        mfa_method: authMethod,
        new_method: false,
        locale: this.locale
      }));
      this.displaySpinner = false;
      if (response.body) {
        if (this.electorDataService.userAuthData()?.mfaMethod === 'SMS') {
          this.errorText = $localize`A new code has been sent. Please note that it can take a few seconds for the SMS to be delivered. If you have not received the SMS, please also check your email for the new code.`;
        } else {
          this.errorText = $localize`A new code has been sent. Please note that it can take a few seconds for the email to be delivered. If you have not received the email, please use the "Contact Support" link for assistance.`;
        }
        this.resetTimer();
        this.openDialog({ type: DialogType.SUCCESS, title: $localize`Code sent`, content: this.errorText });
        return true;
      } else {
        const message = $localize`We could not process your request. Please use the "Contact Support" link for assistance.`;
        this.openDialog({ type: DialogType.ERROR, title: $localize`Unknown error.`, content: message });
        return false;
      }
    } catch (err: any) {
      this.displaySpinner = false;
      this.resetTimer();
      switch (err.error.error_type) {
        case 'MISSING_PHONE':
          this.errorText = $localize`Your phone number could not be found. Please try logging again or use the "Contact Support" link for assistance.`
          this.errorTitle = $localize`Phone Number Unknown.`
          this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
          break
        case 'UNKNOWN_USER':
          this.errorTitle = $localize`Information not found`;
          this.errorText = $localize`The information provided does not match our records. Please verify that you have entered the information correctly. If you believe this is in error, use the "Contact Support" link for assistance.`
          this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText })
          break;
        case 'BAD_REQUEST':
          this.errorText = $localize` Please try again or use the "Contact Support" link for assistance.`;
          this.errorTitle = $localize`Bad Request.`
          this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
          break;
        case 'INVALID_OTP':
          this.electorDataService.userAuthData()!.attemptsRemaining = (this.electorDataService.userAuthData()?.attemptsRemaining ?? 1) - 1;
          this.errorText = $localize`The verification code is incorrect. To continue, please try entering it again.`;
          this.errorTitle = $localize`Invalid Code`
          this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
          break
        case 'OTP_EXPIRED':
          this.electorDataService.userAuthData()!.attemptsRemaining = (this.electorDataService.userAuthData()?.attemptsRemaining ?? 1) - 1;
          if ((this.electorDataService.userAuthData()?.attemptsRemaining ?? 0) <= 0) {
            this.errorText = $localize`You have exhausted attempts to enter the verification code, to continue, please press the OK button to return to the login screen and try to enter again.`;
            this.errorTitle = $localize`Maximum Attempts Reached.`
          } else {
            this.errorText = $localize`The verification code has expired. Please submit a new code. (${this.electorDataService.userAuthData()?.attemptsRemaining} attempts remaining.)`;
            this.errorTitle = $localize`Code Expired.`
          }
          this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
          break;
        case 'RESEND_TOO_SOON':
          this.errorTitle = $localize`Resend too soon.`;
          this.errorText = $localize`Please wait at least ${this.electorDataService.resendExpirationTimeMinutes()} minutes before requesting a new verification code.`;
          this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
          break;
        case 'MAX_OTP_ATTEMPTS':
          this.electorDataService.userAuthData()!.attemptsRemaining = 0;
          this.maxMFACodeAttemptsReached = true;
          this.errorText = $localize`Your account has been temporarily blocked. To unblock it, please use the "Contact Support" link for assistance.`;
          this.errorTitle = $localize`Maximum Attempts Reached.`
          this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
          break;
        case "MAX_OTP_RESENDS":
          this.electorDataService.userAuthData()!.resendsRemaining = 0;
          this.maxMFAResendsReached = true;
          this.errorText = $localize`You have made ${this.electorDataService.userAuthData()?.maxResends ?? 5} resend requests. No more requests are allowed.`;
          this.errorTitle = $localize`Maximum Attempts Reached.`
          this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
          break;
        case "ACCOUNT_LOCKED":
          this.errorText = $localize`Your account has been locked. Please use the "Contact Support" link for assistance.`;
          this.errorTitle = $localize`Locked Account`
          this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
          break;
        case "ALREADY_LOGGED_IN":
          this.errorText = $localize`You already have an active session. To continue, please close the active session and try again.`;
          this.errorTitle = $localize`Already Logged In.`
          this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
          break;

        case 'ELECTION_NOT_YET_OPEN':
          this.errorText = $localize`The election will be open from ${this.aboutService.startTime} to ${this.aboutService.endTime}.`;
          this.errorTitle = $localize`Election not open yet`
          this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
          break;

        case 'ELECTION_CLOSED':
          this.errorText = $localize`The election was open from ${this.aboutService.startTime} to ${this.aboutService.endTime}.`;
          this.errorTitle = $localize`Election Closed.`
          this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
          break;

        case 'MISSING_EMAIL':
          this.errorText = $localize`We could not find an email address on file. Please use the "Contact Support" link for assistance.`;
          this.errorTitle = $localize`Missing Email`
          this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
          break;

        case 'MISSING_PHONE':
          this.errorText = $localize`We could not find a phone number on file. Please use the "Contact Support" link for assistance.`;
          this.errorTitle = $localize`Missing Phone`
          this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
          break;

        case 'INVALID_FORM':
          this.errorText = $localize`We could not process your request. Please use the "Contact Support" link for assistance.`;
          this.errorTitle = $localize`Malformed Data`
          this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
          break;

        case 'DECODER_STATE_LOST':
          this.errorText = $localize`Your session timed out. Please try to submit again or refresh the page.`;
          this.errorTitle = $localize`Verification Not Successful.`
          this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
          break;

        case 'INVALID_MTE_DECODING':
          this.errorText = $localize`Your session timed out. Please try to submit again or refresh the page.`;
          this.errorTitle = $localize`Verification Not Successful.`
          this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
            break;


        default:
          this.errorText = $localize`We could not process your request. Please use the "Contact Support" link for assistance.`
          this.errorTitle = $localize`Unknown error.`
          this.openDialog({ type: DialogType.ERROR, title: this.errorTitle, content: this.errorText });
          break;
      }
      return false;
    }
  }

  openDialog(data: any): void {
    const dialogRef = this.dialog.open(GenericDialogComponent, {
      data: data
    });
  }

  resetMfaInput() {
    this.mfaInputFields.forEach((input) => {
      input.nativeElement.value = '';
    });
    this.mfaFormGroup.get('code')?.reset();
    this.mfaFormGroup.reset();
  }

  resetFocus() {
    let focusField = document.getElementById('accessibility-button')
    let focusFieldMobile = document.getElementById('accessibility-button-mobile')

    if (focusField) {
      focusField.focus()
    }

    if (focusFieldMobile) {
      focusFieldMobile.focus()
    }
  }

  validateNumericInput(value: string): string {
    return /^\d$/.test(value) ? value : '';
  }

  handleInput(event: Event, index: number): void {
    const inputElement = event.target as HTMLInputElement;
    const value = this.validateNumericInput(inputElement.value.slice(0, 1));
    inputElement.value = value;

    if (!value) {
      this.updateFormControl(); 
      return;
    }

    const nextInput = document.querySelectorAll<HTMLInputElement>('#otp > input')[index + 1];
    if (index === 5) {
      inputElement.select(); 
    } else if (nextInput) {
      nextInput.focus(); 
    }

    this.updateFormControl();
  }

  handlePaste(event: ClipboardEvent): void {
    event.preventDefault();
    const clipboardData = event.clipboardData?.getData('text') || '';
    const pastedValues = clipboardData.split('').filter((char) => /^\d$/.test(char)); 

    if (pastedValues.length !== 6) {
      this.mfaFormGroup.get('code')?.setErrors({ invalidPaste: true });
      return;
    }

    const inputs = document.querySelectorAll<HTMLInputElement>('#otp > input');
    inputs.forEach((input, i) => {
      input.value = pastedValues[i] || ''; 
    });

    inputs[inputs.length - 1]?.focus(); 
    this.updateFormControl();
  }

  handleFocus(event: FocusEvent): void {
    const inputElement = event.target as HTMLInputElement;
    inputElement.select(); 
  }

  handleBlur(event: FocusEvent): void {
    const inputElement = event.target as HTMLInputElement;
    inputElement.value = this.validateNumericInput(inputElement.value);
    this.updateFormControl();
  }

  updateFormControl(): void {
    const inputs = document.querySelectorAll<HTMLInputElement>('#otp > input');
    const code = Array.from(inputs)
      .map((input) => input.value)
      .join('');

    const control = this.mfaFormGroup.get('code');
    control?.setValue(code);

    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      control?.setErrors({ invalidCode: true });
    } else {
      control?.setErrors(null);
    }
  }

  handleKeydown(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;
    
    if (event.key === 'Backspace') {
      if (input.value === '' && index > 0) {
        event.preventDefault();
        this.mfaInputFields.get(index - 1)?.nativeElement.focus();
      }
    }
    
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.mfaInputFields.get(index - 1)?.nativeElement.focus();
    }
    
    if (event.key === 'ArrowRight' && index < 5) {
      event.preventDefault();
      this.mfaInputFields.get(index + 1)?.nativeElement.focus();
    }
  }

}
